/**
 * Sound Design Pack — Web Audio synthesized micro-sounds.
 *
 * Singleton AudioContext (same pattern as messaging/notificationSound.ts).
 * Respects user mute preference via localStorage.
 */

const MUTE_KEY = 'brainsway_sound_muted';

let audioCtx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  try {
    if (!audioCtx) audioCtx = new AudioContext();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    return audioCtx;
  } catch {
    return null;
  }
}

function isMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Short sine pop — button click */
function pop(): void {
  const ctx = getCtx();
  if (!ctx || isMuted()) return;
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(600, now);
  osc.frequency.exponentialRampToValueAtTime(400, now + 0.08);
  gain.gain.setValueAtTime(0.12, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.08);
}

/** Two-tone chime — action complete */
function success(): void {
  const ctx = getCtx();
  if (!ctx || isMuted()) return;
  const now = ctx.currentTime;

  // C5
  const osc1 = ctx.createOscillator();
  const g1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.value = 523;
  g1.gain.setValueAtTime(0.1, now);
  g1.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  osc1.connect(g1).connect(ctx.destination);
  osc1.start(now);
  osc1.stop(now + 0.2);

  // E5 (80ms offset)
  const osc2 = ctx.createOscillator();
  const g2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.value = 659;
  g2.gain.setValueAtTime(0.1, now + 0.08);
  g2.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  osc2.connect(g2).connect(ctx.destination);
  osc2.start(now + 0.08);
  osc2.stop(now + 0.2);
}

/** Sawtooth buzz — validation fail */
function error(): void {
  const ctx = getCtx();
  if (!ctx || isMuted()) return;
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const shaper = ctx.createWaveShaper();

  // Soft-clip curve
  const samples = 256;
  const curve = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = (i * 2) / samples - 1;
    curve[i] = Math.tanh(x * 2);
  }
  shaper.curve = curve;

  osc.type = 'sawtooth';
  osc.frequency.value = 150;
  gain.gain.setValueAtTime(0.08, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  osc.connect(shaper).connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.15);
}

/** Quick triangle tick — toggle/checkbox */
function tick(): void {
  const ctx = getCtx();
  if (!ctx || isMuted()) return;
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(1200, now);
  osc.frequency.exponentialRampToValueAtTime(900, now + 0.03);
  gain.gain.setValueAtTime(0.1, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.03);
}

/** White noise whoosh — navigation */
function whoosh(): void {
  const ctx = getCtx();
  if (!ctx || isMuted()) return;
  const now = ctx.currentTime;

  // Generate white noise buffer
  const len = Math.round(ctx.sampleRate * 0.12);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;

  const src = ctx.createBufferSource();
  src.buffer = buf;

  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.Q.value = 1;
  bp.frequency.setValueAtTime(200, now);
  bp.frequency.exponentialRampToValueAtTime(800, now + 0.12);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.06, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

  src.connect(bp).connect(gain).connect(ctx.destination);
  src.start(now);
  src.stop(now + 0.12);
}

function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, String(muted));
  } catch {
    // ignore
  }
}

export const sound = { pop, success, error, tick, whoosh, setMuted, isMuted };
